import { useEffect } from "react";

import { t } from "../../../i18n";
import { trpc } from "../../../lib/trpc";
import { Avatar, Button, Dialog, Icon, Stack } from "../../ui";
import type { Member } from "../types";

interface NotifyMembersDialogProps {
  open: boolean;
  onClose: () => void;
  settlementId: string;
  pendingMembers: Member[];
}

export function NotifyMembersDialog({
  open,
  onClose,
  settlementId,
  pendingMembers,
}: NotifyMembersDialogProps) {
  const notify = trpc.settlement.notify.useMutation();

  // Reset transient state whenever the dialog is reopened so old results
  // don't bleed across uses.
  useEffect(() => {
    if (!open) notify.reset();
  }, [open, notify]);

  const handleSend = () => {
    if (pendingMembers.length === 0) return;
    notify.mutate({ settlementId });
  };

  const result = notify.data;
  const showResult = !!result;
  const hasSent = !!result && result.sent > 0;
  const hasFailures = !!result && result.failed > 0;
  const allSkipped = !!result && result.skipped > 0 && result.sent === 0 && result.failed === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title={t.settlement.summary.notifyDialog.title}
      description={t.settlement.summary.notifyDialog.description}
      footer={
        showResult ? (
          <Button onClick={onClose} variant="primary">
            {t.common.close}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={notify.isPending}>
              {t.common.cancel}
            </Button>
            <Button
              variant="primary"
              iconLeft="mail"
              isLoading={notify.isPending}
              disabled={pendingMembers.length === 0}
              onClick={handleSend}
            >
              {notify.isPending
                ? t.settlement.summary.notifyDialog.sending
                : t.settlement.summary.notifyDialog.submit}
            </Button>
          </>
        )
      }
    >
      <Stack gap={3}>
        {showResult ? (
          <>
            {allSkipped && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/30">
                <Icon
                  name="alert-circle"
                  size="sm"
                  className="mt-0.5 text-amber-600 dark:text-amber-300"
                />
                <div className="min-w-0">
                  <div className="font-medium text-amber-900 text-sm dark:text-amber-100">
                    {t.settlement.summary.notifyDialog.skippedTitle}
                  </div>
                  <p className="mt-0.5 text-amber-800 text-xs dark:text-amber-200">
                    {t.settlement.summary.notifyDialog.skippedDescription}
                  </p>
                </div>
              </div>
            )}
            {hasSent && !hasFailures && (
              <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-900/30">
                <Icon
                  name="check-circle"
                  size="sm"
                  className="mt-0.5 text-emerald-600 dark:text-emerald-300"
                />
                <div className="min-w-0">
                  <div className="font-medium text-emerald-900 text-sm dark:text-emerald-100">
                    {t.settlement.summary.notifyDialog.successTitle}
                  </div>
                  <p className="mt-0.5 text-emerald-800 text-xs dark:text-emerald-200">
                    {t.settlement.summary.notifyDialog.successDescription(result.sent)}
                  </p>
                </div>
              </div>
            )}
            {hasSent && hasFailures && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/30">
                <Icon
                  name="alert-circle"
                  size="sm"
                  className="mt-0.5 text-amber-600 dark:text-amber-300"
                />
                <div className="min-w-0">
                  <div className="font-medium text-amber-900 text-sm dark:text-amber-100">
                    {t.settlement.summary.notifyDialog.partialFailureTitle}
                  </div>
                  <p className="mt-0.5 text-amber-800 text-xs dark:text-amber-200">
                    {t.settlement.summary.notifyDialog.partialFailureDescription(
                      result.sent,
                      result.failed,
                    )}
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <p className="mb-2 font-medium text-ink-600 text-xs">
                {t.settlement.summary.notifyDialog.recipientsHeading}
              </p>
              {pendingMembers.length === 0 ? (
                <p className="text-ink-500 text-sm">
                  {t.settlement.summary.notifyDialog.emptyRecipients}
                </p>
              ) : (
                <ul className="space-y-1.5">
                  {pendingMembers.map((m) => (
                    <li key={m.userId} className="flex items-center gap-2">
                      <Avatar name={m.displayName ?? m.email} size="sm" />
                      <span className="truncate text-ink-800 text-sm">
                        {m.displayName ?? m.email}
                      </span>
                      <span className="truncate text-ink-400 text-xs">{m.email}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {notify.isError && (
              <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 dark:border-rose-800 dark:bg-rose-900/30">
                <Icon
                  name="alert-circle"
                  size="sm"
                  className="mt-0.5 text-rose-600 dark:text-rose-300"
                />
                <div className="min-w-0">
                  <div className="font-medium text-rose-900 text-sm dark:text-rose-100">
                    {t.settlement.summary.notifyDialog.failureTitle}
                  </div>
                  <p className="mt-0.5 text-rose-800 text-xs dark:text-rose-200">
                    {t.settlement.summary.notifyDialog.failureDescription(notify.error.message)}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </Stack>
    </Dialog>
  );
}
